import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import styles from "./ImmunizationMMRViewNew.module.css";

import { AddButton, InputBox } from "@cpnw/ui";
import DashboardRadio from "components/DashboardRadio";
import FormDatePicker from "components/FormDatePicker";
import classNames from "classnames";

const SubmissionTypes = {
  A: "A",
  B: "B",
  C: "C",
  D: "D",
  Military: "Military",
};

const SubmissionTypeDescriptions = {
  A: "A. Vaccination Dates:",
  B: "B. Immunity by Titer:",
  Military: "Military immunization",
};

const MMRTestResults = {
  Neg: "Neg/Equivalent",
  Pos: "Pos",
};

// ---- Virus keys ----
const VIRUS_MEASLES = "measles";
const VIRUS_MUMPS = "mumps";
const VIRUS_RUBELLA = "rubella";

export const ImmunizationMMRViewNew = forwardRef(
  (
    {
      data = {
        type: null,
        date1: null,
        date2: null,
        measles: [],
        mumps: [],
        rubella: [],
      },
      onChange,
      disabled = false,
    },
    ref
  ) => {
    const setValue = (key, value) => {
      if (key) {
        data[key] = value;
      }

      if (onChange) {
        onChange({ ...data });
      }
    };

    const addEntry = (field) => {
      const list = [...(data[field] || [])];
      list.push({
        localId: `${field}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
        date: null,
        result: null,
      });
      setValue(field, list);
    };

    const updateEntry = (field, index, subKey, value) => {
      const list = [...(data[field] || [])];
      list[index][subKey] = value;
      setValue(field, list);
    };

    const removeEntry = (field, index) => {
      const list = [...(data[field] || [])];
      list.splice(index, 1);
      setValue(field, list);
    };

    const isPersisted = (entry) => entry && entry.id != null; // row came from DB (has id)
    const canShowRemoveX = (entry, index, disabled) => {
      const isBoosterRow = index > 0;
      const isNew = entry && entry.id == null; // not persisted
      return !disabled && isBoosterRow && isNew;
    }; // not first row, unsaved this session

    useImperativeHandle(ref, () => ({
      validator: () => {
        if (data.type === SubmissionTypes.Military) {
          clearTiterFields();
          clearVaccinationFields();
        } else if (data.type === SubmissionTypes.A) {
          clearTiterFields();
        } else if (data.type === SubmissionTypes.B) {
          clearVaccinationFields();
        }
      },
    }));

    const clearTiterFields = () => {
      setValue(VIRUS_MEASLES, []);
      setValue(VIRUS_MUMPS, []);
      setValue(VIRUS_RUBELLA, []);
    };

    const clearVaccinationFields = () => {
      setValue("date1", null);
      setValue("date2", null);
    };

    const renderOptionMilitary = () => {
      return (
        <div>
          <DashboardRadio
            isSelected={data.type === SubmissionTypes.Military}
            onChange={(value) => {
              setValue("type", SubmissionTypes.Military);
            }}
            value={SubmissionTypes.Military}
            label={SubmissionTypeDescriptions.Military}
            containerClassName={styles.radioContainer}
            disabled={disabled}
          />
        </div>
      );
    };

    const renderOptionA = () => {
      return (
        <div>
          <DashboardRadio
            isSelected={data.type === SubmissionTypes.A}
            onChange={(value) => {
              setValue("type", SubmissionTypes.A);
            }}
            value={SubmissionTypes.A}
            label={SubmissionTypeDescriptions.A}
            containerClassName={styles.radioContainer}
            disabled={disabled}
          />
          {data.type === SubmissionTypes.A && (
            <div className={styles.testRow}>
              <div className={styles.testRow}>
                1.{" "}
                <FormDatePicker
                  dateFormat="MM/dd/yyyy"
                  dateString={data.date1}
                  setDate={(value) => {
                    data.date1 = value;
                    setValue("date1", value);
                  }}
                  disabled={disabled}
                />
              </div>
              <div className={styles.testRow}>
                2.{" "}
                <FormDatePicker
                  dateFormat="MM/dd/yyyy"
                  dateString={data.date2}
                  setDate={(value) => {
                    data.date2 = value;
                    setValue("date2", value);
                  }}
                  disabled={disabled}
                />
              </div>
            </div>
          )}
        </div>
      );
    };

    const renderOptionB = () => {
      return (
        <div>
          <DashboardRadio
            isSelected={data.type === SubmissionTypes.B}
            onChange={() => {
              setValue("type", SubmissionTypes.B);

              [VIRUS_MEASLES, VIRUS_MUMPS, VIRUS_RUBELLA].forEach((f) => {
                const list = data[f] || [];

                if (list.length === 0) {
                  const defaultResult = null; // default to no selection

                  setValue(f, [{ date: null, result: defaultResult }]);
                } else {
                  // Row exists → backfill defaults only if empty
                  if (f === VIRUS_MEASLES && list[0]?.result == null) {
                    const copy = [...list];
                    copy[0] = { ...copy[0], result: MMRTestResults.Neg };
                    setValue(VIRUS_MEASLES, copy);
                  }
                  if (f === VIRUS_RUBELLA && list[0]?.result == null) {
                    const copy = [...list];
                    copy[0] = { ...copy[0], result: MMRTestResults.Pos };
                    setValue(VIRUS_RUBELLA, copy);
                  }
                }
              });
            }}
            value={SubmissionTypes.B}
            label={SubmissionTypeDescriptions.B}
            containerClassName={styles.radioContainer}
            disabled={disabled}
          />

          {data.type === SubmissionTypes.B && (
            <div className={classNames(styles.lv2)}>
              {renderTiterBlock(VIRUS_MEASLES, "Measles by titer:")}
              {renderTiterBlock(VIRUS_MUMPS, "Mumps by titer:")}
              {renderTiterBlock(VIRUS_RUBELLA, "Rubella by titer:")}
            </div>
          )}
        </div>
      );
    };

    const renderTiterBlock = (field, label) => {
      const entries = data[field] || [];
      const initialTiter = entries[0];
      const boosters = entries.slice(1) || [];
      const latestIndex = entries.length - 1;

      return (
        <div className={styles.testGroup}>
          <label className={styles.titerLabel}>{label}</label>

          {initialTiter ? (
            <div className={styles.entryRow}>
              {renderTiterEntry(field, initialTiter, 0, latestIndex === 0)}
            </div>
          ) : null}

          {renderTiterBoosters(field, boosters, latestIndex)}

          {renderAddBoosterButton(entries, field)}
        </div>
      );
    };

    const renderTiterBoosters = (field, boosters, latestIndex) => {
      if (!boosters || boosters.length === 0) return null;

      return (
        <div>
          {/* show only once */}
          <div className={styles.boosterLabelRow}>
            <span className={styles.boosterDateLabel}>Booster Date:</span>
          </div>

          {(boosters || []).map((entry, boosterIndex) => {
            const actualIndex = boosterIndex + 1; // because boosters start after initial row
            const isLatest = actualIndex === latestIndex;

            return (
              <div
                key={entry?.id ?? entry?.localId ?? `${field}-${actualIndex}`}
                className={styles.entryRow}
              >
                {renderTiterEntry(field, entry, actualIndex, isLatest)}
              </div>
            );
          })}
        </div>
      );
    };

    const renderTiterEntry = (field, entry, indexForRadio, isLatest) => {
      const entryDisabled = disabled || !isLatest;

      return (
        <div className={styles.titerGridRow}>
          <div className={styles.dateColumn}>
            <FormDatePicker
              dateFormat="MM/dd/yyyy"
              dateString={entry.date}
              setDate={(value) => updateEntry(field, indexForRadio, "date", value)}
              disabled={entryDisabled}
            />
          </div>

          <div className={styles.resultColumn}>
            {renderTiterResultRadio(field, entry, indexForRadio, entryDisabled, isLatest)}
          </div>
        </div>
      );
    };

    const renderTiterResultRadio = (field, entry, indexForRadio, entryDisabled, isLatest) => {
      return (
        <>
          <span className={styles.resultLabel}>Result:</span>

          <DashboardRadio
            name={`${field}-result-${indexForRadio}`}
            isSelected={entry.result === MMRTestResults.Neg}
            onChange={() => updateEntry(field, indexForRadio, "result", MMRTestResults.Neg)}
            value={MMRTestResults.Neg}
            label={MMRTestResults.Neg}
            containerClassName={styles.radioContainer}
            disabled={entryDisabled}
          />

          <DashboardRadio
            name={`${field}-result-${indexForRadio}`}
            isSelected={entry.result === MMRTestResults.Pos}
            onChange={() => updateEntry(field, indexForRadio, "result", MMRTestResults.Pos)}
            value={MMRTestResults.Pos}
            label={MMRTestResults.Pos}
            containerClassName={styles.radioContainer}
            disabled={entryDisabled}
          />

          {canShowRemoveX(entry, indexForRadio, disabled) && (
            <button
              type="button"
              onClick={() => removeEntry(field, indexForRadio)}
              className={styles.removeButton}
              aria-label="Remove titer entry"
              title="Remove"
            >
              ×
            </button>
          )}
        </>
      );
    };

    const renderAddBoosterButton = (entries, field) => {
      const last = entries[entries.length - 1];
      const isNeg = last?.result === MMRTestResults.Neg;
      return !disabled && isNeg ? (
        <AddButton onClick={() => addEntry(field)} label="Add Booster" />
      ) : null;
    };

    return (
      <div className={styles.container}>
        {renderOptionMilitary()}
        {renderOptionA()}
        {renderOptionB()}
      </div>
    );
  }
);
